import React, { useContext, useState } from 'react';
import styled from '@emotion/styled';
import Divider from '@mui/material/Divider';
import Pagination from '@mui/material/Pagination';
import mainContext from '../../contexts/mainContext';
import { __constants, __jobStatus, __snackBarSeverity, __snackBarText } from '../../constants';
import { getJobsController } from '../../Controllers/BookingManagement';
import Filter from './commonFilter'; // Import the common filter

const CommonDataTable = ({
  type, // "Booking" or "Job"
  profileNavTitle,
  headers,
  renderRow,
  onRowClick,
  canvasComponent: CanvasComponent,
  canvasState,
  setCanvasState,
  additionalParams = {},
  customGridColumns = "1.6fr 1fr 3fr 1.8fr 1.3fr 2.5fr 1.5fr 0.8fr"
}) => {
  const { setProfileNavTitle, setSnackBar, setPageload } = useContext(mainContext);
  const [filterState, setFilterState] = React.useState({});
  const [data, setData] = useState([]);
  const [metaData, setMetaData] = useState(null);

  const callSnackBar = (msg, type) => {
    setSnackBar({
      status: true,
      text: msg,
      severity: __snackBarSeverity[type]
    });
  };

  const statusClassMap = {
    [__jobStatus.posted]: "status_pending",
    [__jobStatus.hired]: "status_processing",
    [__jobStatus.completed]: "status_processing",
    [__jobStatus.finalized]: "status_active",
    [__jobStatus.cancelled]: "status_cancel",
    [__jobStatus.expired]: "status_expired",
    [__jobStatus.declined]: "status_declined",
    [__jobStatus.refundByNona]: "status_refunded",
    [__jobStatus.partiallyRefundByNona]: "status_partially_refunded",
  };

  const getData = async (page = 1, filterApply = true) => {
    setPageload(true);
    let param = null;
    
    if (filterApply) {
      if (filterState.status) {
        param = { page: page, ...filterState, ...additionalParams, type: type };
      } else {
        param = { page: page, ...filterState, ...additionalParams, status: "", type: type };
      }
    } else {
      param = { page: page, ...additionalParams, status: "", type: type };
    }

    const res = await getJobsController(param);
    setPageload(false);
    
    if (res && res.status === 500) {
      return callSnackBar(__snackBarText.internalServerError, "error");
    }
    if (res.data.status !== 200) {
      return callSnackBar(res.data.errors[0].message, "error");
    }

    setData(res.data.data.allJobs[0].data);
    setMetaData(res.data.data.allJobs[0].metadata[0]);
  };

  const handlePaginationClick = (e, page) => {
    getData(page);
  };

  React.useEffect(() => {
    getData();
    setProfileNavTitle(profileNavTitle);
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {CanvasComponent && (
        <CanvasComponent
          canvasState={canvasState}
          setCanvasState={setCanvasState}
        />
      )}
      <Container className='scrollbar'>
        <Header>
          <ListGrid gridColumns={customGridColumns}>
            {headers.map((header, index) => (
              <li key={index} style={header.style || {}}>
                {header.label}
              </li>
            ))}
            <li style={{ display: "flex", justifyContent: "center" }}>
              <Filter
                setFilterState={setFilterState}
                filterState={filterState}
                callFunction={getData}
              />
            </li>
          </ListGrid>
        </Header>
        <Body>
          <Divider />
          {data.map((item, index) => (
            <ListGrid key={index} gridColumns={customGridColumns}>
              {renderRow(item, statusClassMap, onRowClick, setCanvasState)}
              <li className='list_card_view_btn' tabIndex={1} onClick={() => {
                if (onRowClick) {
                  onRowClick(item, setCanvasState);
                }
              }}>
                View
              </li>
            </ListGrid>
          ))}
        </Body>
        {data.length === 0 && <div className='no-record'><span>No Record Found</span></div>}
      </Container>
      {data.length > 0 && (
        <>
          <Pagination 
            count={Math.ceil((metaData?.total ?? 1) / __constants.listingLimit)} 
            color="primary" 
            shape="rounded" 
            onChange={(e, page) => { handlePaginationClick(e, page) }} 
          />
          <br />
        </>
      )}
    </>
  );
};

const Container = styled("div")`
  overflow-x: auto;
  padding-top: 1rem;
  margin-bottom: 1rem;
  font-size: 85%;
  min-height: 80vh;
`;

const Header = styled("div")`
  margin: 0.5rem 0;
  ul li{
    font-family: "semi-bold" !important;
  }
`;

const Body = styled("div")`
  margin-top: 1rem;
  display: grid;
  row-gap: 1.6rem;
`;

const ListGrid = styled("ul")`
  min-width: 1200px;
  display: grid;
  column-gap: 0.7rem;
  grid-template-columns: ${props => props.gridColumns};
  align-items: center;
  padding: 3px !important;
  &:hover{
    background-color: rgba(0,0,0,0.04);
    border-radius: 8px;
  }
`;

export default CommonDataTable;